import { forwardRef, useEffect, useState } from "react";
import waterpipe from "waterpipe";
import dom from "zeta-dom/dom";
import { combineFn, setTimeout } from "zeta-dom/util";
import { toRefCallback, useFormField, useRefInitCallback } from "zeta-dom-react";
import { TextField, TextFieldProps, validators } from ".";

const daysInMonths = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const pattern = '__ / __';
const emptyStr = pattern.replace(/_/g, ' ');

function normalizeValue(value: string, force?: boolean) {
    // extract month and date from 'MM / DD' and apply upper bounds
    let sd = '', sm = '';
    value.replace(/\d{1,2}/g, (v, i) => {
        if (i) {
            sd = v;
        } else {
            sm = v;
        }
        return '';
    });

    let m = Math.min(+sm, 12);
    let d = Math.min(+sd, ((force || sm.length > 1) && daysInMonths[m - 1]) || 31);
    return value.replace(/\d{1}\s|\d{2}/g, (v, i) => {
        // keep current text for one-digit number to avoid interfering with typing
        // unless when input field is not focused
        if (!force && v[1] === ' ') {
            return v;
        } else {
            return waterpipe('{{:printf %02d}}', (i ? d : m) || 1);
        }
    });
}

function convertToDisplayFormat(value: string) {
    // convert '9999-MM-DD' to 'MM / DD'
    return /9999-(\d{2})-(\d{2})/.test(value) ? waterpipe('{{join " / "}}', [RegExp.$1, RegExp.$2]) : '';
}

function convertFromDisplayFormat(value: string) {
    // convert 'MM / DD' to '9999-MM-DD'
    return /(\d{2})\D*(\d{2})/.test(value) ? waterpipe('9999-{{join -}}', [RegExp.$1, RegExp.$2]) : '';
}

export const DOBField = forwardRef<HTMLDivElement, TextFieldProps>((props, ref) => {
    props = {
        ...props,
        onValidate: validators.combine(
            props.required && validators.required(),
            props.onValidate
        )
    };

    // pull name out from props so that the underlying text box
    // will not attach events to FormContext
    const { name, onChange: _, ..._props } = props;
    const { value, error, setValue, elementRef } = useFormField(props, '');
    const [displayValue, setDisplayValue] = useState('');

    useEffect(() => {
        // normalize value first to avoid invalid calendar date
        // if the new value was invalid then update it to the corrected one
        // otherwise update display value if needed
        const dvalue = normalizeValue(convertToDisplayFormat(value), true);
        const nvalue = convertFromDisplayFormat(dvalue);
        if (nvalue !== value) {
            setValue(nvalue);
        } else {
            setDisplayValue(v => {
                if (convertFromDisplayFormat(v) !== value) {
                    return dvalue;
                }
                return v;
            });
        }
    }, [value, setValue]);

    ref = combineFn(elementRef, toRefCallback(ref), useRefInitCallback(initEvent));
    return (
        <TextField ref={ref} {..._props} error={error} value={displayValue}
            inputMode="numeric" onChange={onChange} />
    );

    function onChange(value1: string) {
        setDisplayValue(value1);
        setValue(convertFromDisplayFormat(value1));
    }

    function onInputFocusIn(input: HTMLInputElement) {
        if (!input.value) {
            input.value = emptyStr;
        }
        setTimeout(() => {
            let pos = 0;
            for (let i = pattern.length - 1; i >= 0; i--) {
                if (pattern[i] === '_' && input.value[i] === ' ') {
                    pos = i;
                }
            }
            input.setSelectionRange(pos, pos);
            onChange(input.value);
        });
    }

    function onInputFocusOut(input: HTMLInputElement) {
        if (input.value === emptyStr || !input.value) {
            input.value = '';
            onChange('');
        } else {
            onChange(normalizeValue(input.value, true));
        }
    }

    function onInputTextInput(e: Zeta.ZetaEventType<"textInput", Zeta.ZetaDOMEventMap, HTMLInputElement>, input: HTMLInputElement) {
        if (!input.value) {
            input.value = emptyStr;
        }
        let v = input.value;
        let a = input.selectionStart || 0;
        let b = input.selectionEnd || 0
        if (a !== b) {
            v = v.slice(0, a) + emptyStr.slice(a, b) + v.slice(b);
        }
        let text = e.data.replace(/\D/g, '');
        let chars = v.split('');
        let pos = chars.length;
        for (let i = 0, j = 0, len = pattern.length; i < len; i++) {
            if (pattern[i] === '_' && (chars[i] === ' ' || !chars[i])) {
                chars[i] = text[j++] || ' ';
                if (chars[i] === ' ') {
                    pos = i;
                    break;
                }
            }
        }
        input.value = normalizeValue(chars.join(''));
        input.setSelectionRange(pos, pos);
        onChange(input.value);
        e.handled();
        e.preventDefault();
    }

    function onInputKeystroke(e: Zeta.ZetaEventType<"keystroke", Zeta.ZetaDOMEventMap, HTMLInputElement>, input: HTMLInputElement) {
        let a = input.selectionStart || 0;
        let b = input.selectionEnd || 0
        if (a === b) {
            if (e.data === 'backspace') {
                a = Math.max(0, pattern.lastIndexOf('_', a - 1));
            } else {
                b = pattern.indexOf('_', b + 1);
                if (b < 0) {
                    b = pattern.length;
                }
            }
        }

        if (a !== b) {
            input.setRangeText(emptyStr.slice(a, b), a, b);
            input.setSelectionRange(a, a);
            onChange(input.value);
        }
        e.handled();
    }

    function initEvent(element: HTMLElement) {
        const input = element.querySelector('input')!;
        dom.on(input, 'focusin', () => {
            onInputFocusIn(input);
        });
        dom.on(input, 'focusout', () => {
            onInputFocusOut(input);
        });
        dom.on(input, 'textInput', e => {
            onInputTextInput(e, input);
        });
        dom.on(input, 'keystroke', e => {
            if (e.data === 'backspace' || e.data === 'delete') {
                onInputKeystroke(e, input)
            }
        });
    }
});
